<template>
  <div class="home">
    <div class="scroll-progress" :style="{ width: `${progress}%` }" aria-hidden="true" />

    <!-- 히어로 -->
    <section class="hero hero--video" aria-labelledby="hero-title">
      <video
        ref="heroVideoEl"
        class="hero-video"
        :autoplay="!reduceMotion"
        muted
        loop
        playsinline
        preload="auto"
        poster="/main/hero-poster.jpg"
        aria-hidden="true"
        @loadedmetadata="applyHeroPlaybackRate"
        @play="applyHeroPlaybackRate"
      >
        <source src="/main/hero.mp4" type="video/mp4" />
      </video>
      <div class="hero-overlay" aria-hidden="true" />
      <div class="home-wrap hero-in">
        <div class="hero-left">
          <p class="hero-kicker"><span class="kicker-line" aria-hidden="true" />장기요양기관의 운영과 성장을 돕는 전문 파트너</p>
          <h1 id="hero-title">운영은 더 쉽게.<br />경영은 더 든든하게.<br />성장은 함께.</h1>
          <p class="hero-desc">복잡한 임금관리와 재무회계의 부담을 덜고,<br />수급자 발굴과 만족도 향상을 위한 방법까지.<br />기관의 더 나은 내일을 장기요양지원센터가 함께 준비합니다.</p>
          <ul class="hero-criteria" aria-label="장기요양지원센터의 핵심 가치">
            <li><span>01</span>간편함</li>
            <li><span>02</span>안정감</li>
            <li><span>03</span>성장</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 지원 서비스 요약 -->
    <section class="entry" aria-labelledby="entry-title">
      <div class="home-wrap entry-in">
        <span id="entry-title" class="entry-label">지원 서비스</span>
        <div class="entry-items">
          <article v-for="item in serviceSummaries" :key="item.label" class="entry-item">
            <b>{{ item.label }}</b>
            <span>{{ item.desc }}</span>
          </article>
        </div>
      </div>
    </section>

    <!-- 소개 -->
    <section id="brand" class="brand" aria-labelledby="brand-title">
      <div class="home-wrap reveal">
        <p class="sup">001 / BRAND MANIFESTO</p>
        <h2 id="brand-title">기관 운영의 부담은 덜고,<br /><span>성장의 가능성은 더하고.</span></h2>
        <p class="brand-lead">복잡한 운영업무에 쏟는 수고는 줄이고, 돌봄의 품질과 기관의 미래에 더 집중할 수 있도록. 장기요양지원센터는 운영을 돕는 데서 멈추지 않고, 성장의 방법까지 함께 고민합니다.</p>
      </div>
    </section>

    <!-- 핵심 가치 -->
    <section id="values" class="values" aria-labelledby="values-title">
      <div class="home-wrap">
        <div class="section-head reveal">
          <div>
            <p class="sup">002 / THE THREE VALUES</p>
            <h2 id="values-title">장기요양지원센터가 만드는<br />세 가지 변화.</h2>
          </div>
          <p class="side">기능보다 중요한 것은 기관이 얻는 변화입니다. 운영의 간편함, 경영의 안정감, 그리고 성장의 가능성.</p>
        </div>
        <div class="values-list">
          <article v-for="value in values" :key="value.no" class="value-row reveal">
            <p class="value-no">{{ value.no }}</p>
            <div class="value-copy">
              <h3>{{ value.title }}</h3>
              <p class="short">{{ value.short }}</p>
              <p class="desc">{{ value.desc }}</p>
              <ul class="value-tags" :aria-label="`${value.title} 핵심 내용`">
                <li v-for="tag in value.tags" :key="tag">{{ tag }}</li>
              </ul>
            </div>
            <div class="value-visual">
              <img :src="value.image" :alt="value.alt" loading="lazy" />
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- 강점 -->
    <section id="why" class="why" aria-labelledby="why-title">
      <div class="home-wrap why-in">
        <div class="why-left reveal">
          <p class="sup">003 / WHY HAMWOORI</p>
          <h2 id="why-title">운영을 넘어,<br /><span class="line">성장까지</span><br />생각합니다.</h2>
          <p>장기요양지원센터는 장기요양기관이 실제로 마주하는 업무와 고민을 이해하고, 실무에 도움이 되는 서비스와 콘텐츠를 연결합니다.</p>
        </div>
        <div class="why-list reveal">
          <div v-for="(reason, index) in reasons" :key="reason.title" class="why-item">
            <span class="number">0{{ index + 1 }}</span>
            <div>
              <b>{{ reason.title }}</b>
              <p>{{ reason.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 이용 안내 -->
    <section id="process" class="process" aria-labelledby="process-title">
      <div class="home-wrap reveal">
        <p class="sup">004 / HOW TO BEGIN</p>
        <h2 id="process-title">시작은 필요한 것부터,<br />차근차근.</h2>
        <div class="steps">
          <div v-for="(step, index) in steps" :key="step.title" class="step">
            <p class="n">STEP 0{{ index + 1 }} /</p>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 마무리 -->
    <section class="final" aria-labelledby="final-title">
      <div class="shape" aria-hidden="true" />
      <div class="home-wrap reveal">
        <p class="small">THE NEXT CHAPTER STARTS HERE</p>
        <h2 id="final-title">더 쉬운 운영.<br /><em>더 나은 성장.</em></h2>
        <p>장기요양기관의 오늘과 내일을<br />장기요양지원센터가 함께합니다.</p>
        <div class="buttons">
          <router-link v-if="isAdminLoggedIn" to="/info" class="btn btn-primary">정보마당 둘러보기 <span aria-hidden="true">↗</span></router-link>
        </div>
        <p class="photo-note">본 페이지의 이미지는 브랜드 메시지를 표현하기 위한 AI 제작 이미지입니다. 실제 서비스 범위는 상담 시 확인해 주세요.</p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAdminAuth } from '@/composables/useAdminAuth'

const { isAdminLoggedIn } = useAdminAuth()
const reduceMotion = ref(
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
)
const heroVideoEl = ref<HTMLVideoElement | null>(null)

function applyHeroPlaybackRate() {
  const video = heroVideoEl.value
  if (!video) return
  video.defaultPlaybackRate = 0.5
  video.playbackRate = 0.5
}
const progress = ref(0)
const route = useRoute()
let observer: IntersectionObserver | null = null

const serviceSummaries = [
  { label: '임금관리', desc: '복잡한 운영업무를 더 쉽게' },
  { label: '재무회계', desc: '기관 경영을 더 든든하게' },
  { label: '성장지원', desc: '수급자 발굴과 기관 성장 지원' },
]

const values = [
  {
    no: '01 / SIMPLICITY',
    title: '간편함.',
    short: '복잡한 운영업무를 더 쉽게',
    desc: '임금관리와 재무회계의 반복적인 실무를 정리하고, 기관 운영에 필요한 시간을 보다 효율적으로 사용할 수 있도록 돕습니다.',
    tags: ['임금관리', '재무회계', '업무 간소화'],
    image: '/main/simplicity.jpg?v=3',
    alt: '어지러운 서류 더미 옆에 파일 하나가 놓인 장면 — 복잡한 업무가 한곳으로',
  },
  {
    no: '02 / STABILITY',
    title: '안정감.',
    short: '기관 경영을 더 든든하게',
    desc: '장기요양기관의 업무 흐름을 이해하는 체계적인 지원으로 기관장과 실무자가 경영의 기본에 더 집중할 수 있도록 돕습니다.',
    tags: ['장기요양기관 특화', '체계적인 지원', '실무 중심'],
    image: '/main/stability.jpg?v=3',
    alt: '땅에 단단히 앉은 건물, 창에 불이 고르게 켜진 장면 — 흔들림 없는 안정감',
  },
  {
    no: '03 / GROWTH',
    title: '성장.',
    short: '수급자 발굴과 기관 성장 지원',
    desc: '새로운 수급자를 만나고 기존 수급자·보호자와의 관계를 더 깊게 만들 수 있도록, 현장에서 실천할 수 있는 마케팅 콘텐츠를 제공합니다.',
    tags: ['수급자 발굴', '만족도 향상', '마케팅 콘텐츠'],
    image: '/main/growth.jpg?v=3',
    alt: '열린 문으로 사람들이 들어가는 장면 — 새로운 만남과 기관의 성장',
  },
]

const reasons = [
  { title: '장기요양기관에 집중합니다.', desc: '방문요양·주야간보호·요양시설의 운영 특성을 고려합니다.' },
  { title: '실무에서 바로 이해할 수 있게.', desc: '복잡한 설명보다 실제 적용할 수 있는 정보와 방법을 지향합니다.' },
  { title: '운영과 성장을 함께 바라봅니다.', desc: '행정업무의 효율부터 새로운 수급자와의 만남까지 생각합니다.' },
  { title: '기관의 내일을 위한 콘텐츠.', desc: '기관장이 직접 실천할 수 있는 성장지원 아이디어를 나눕니다.' },
]

const steps = [
  { title: '상담 문의', desc: '기관에서 고민하는 운영·성장 과제를 이야기합니다.' },
  { title: '기관 상황 확인', desc: '기관 유형과 현재 필요한 지원 내용을 살펴봅니다.' },
  { title: '서비스 선택', desc: '임금관리·재무회계 등 필요한 서비스 범위를 확인합니다.' },
  { title: '성장 콘텐츠 활용', desc: '기관에서 활용할 마케팅 자료와 운영 노하우를 살펴봅니다.' },
]

function updateProgress() {
  const max = document.documentElement.scrollHeight - window.innerHeight
  progress.value = max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0
}

onMounted(() => {
  const nodes = document.querySelectorAll('.home .reveal')
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduced || !('IntersectionObserver' in window)) {
    nodes.forEach((el) => el.classList.add('is-in'))
  } else {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-in')
        observer?.unobserve(entry.target)
      })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    nodes.forEach((el) => observer?.observe(el))
  }
  applyHeroPlaybackRate()
  window.addEventListener('scroll', updateProgress, { passive: true })
  updateProgress()
  const id = route.hash.replace('#', '')
  if (id) {
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }))
  }
})

onUnmounted(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', updateProgress)
})
</script>

<style lang="scss" src="./DashboardPage.scss"></style>
